import React, { useState, useEffect, useCallback, useRef } from 'react';
import { T, API_BASE } from '../constants';

// KUNBI Monitor — both-directions NQ book (MW 1-min + 3-min filter, backend ATR
// exits). Polls /strategies/api/strategies/kunbi/decisions/ every 20s. Every
// signal is logged (TAKE/SKIP); a TAKE's exit is backfilled by the per-minute
// walker, so STATUS reads OPEN until a bracket fires, then the exit reason.
//   LONG  : target +1.5ATR / stop -2.0ATR (first-touch, stop-first)
//   SHORT : init +1.5ATR / trail running_low+2.5ATR (init-stop / trail-stop)
// Cert KUNBI-v1-20260618-EA615CBE · Phase 0 (shadow log) · advisory/LOG.

const REFRESH_MS = 20000;
const KUNBI_COLOR = '#d97706';   // amber

// STATUS = OPEN (in-slot) / the exit reason / SKIP. Greens = profit-side exits,
// reds = the immediate hard stops, gold = time/skip.
const STATUS_COLORS = {
  OPEN:        '#3b82f6',
  TARGET:      T.bull,
  TRAIL_STOP:  T.bull,
  STOP:        T.bear,
  INIT_STOP:   T.bear,
  TIME:        T.gold,
  SKIP:        T.textMuted,
};
const STATUS_LABEL = {
  TRAIL_STOP: 'TRAIL-STOP', INIT_STOP: 'INIT-STOP',
};

function fmtET(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    const time = d.toLocaleTimeString('en-GB', {
      timeZone: 'America/New_York', hour12: false,
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    return `${date} ${time}`;
  } catch { return iso; }
}
const todayETStr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

function Pill({ value, color, mono = false }) {
  if (value == null || value === '') return <span style={{ color: T.textMuted }}>—</span>;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
      background: `${color || T.border}22`, border: `1px solid ${color || T.border}66`,
      color: color || T.text, fontFamily: mono ? 'Roboto Mono, monospace' : 'inherit',
    }}>{String(value)}</span>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ flex: '1 1 120px', padding: '12px 14px', borderRadius: 6,
                  background: 'rgba(255,255,255,0.02)', border: `1px solid ${T.border}` }}>
      <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 700,
                    letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 22, color: color || T.text,
                    fontFamily: 'Roboto Mono, monospace', marginTop: 4 }}>{value}</div>
    </div>
  );
}

const num = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));

export default function KunbiMonitor() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [actionFilter, setActionFilter] = useState('');   // '' | TAKE | SKIP
  const [dirFilter, setDirFilter] = useState('');          // '' | LONG | SHORT
  const [limit, setLimit] = useState(100);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const timerRef = useRef(null);

  const fetchRows = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let url = `${API_BASE}/strategies/api/strategies/kunbi/decisions/?limit=${limit}`;
      if (actionFilter) url += `&action=${actionFilter}`;
      if (dirFilter) url += `&dir=${dirFilter}`;
      if (dateFrom) { url += `&date_from=${dateFrom}&date_to=${dateTo || dateFrom}`; }
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      if (!j.ok) throw new Error(j.error || 'unknown');
      setRows(j.decisions || []);
      setLastFetched(new Date());
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  }, [actionFilter, dirFilter, limit, dateFrom, dateTo]);

  useEffect(() => {
    fetchRows();
    timerRef.current = setInterval(fetchRows, REFRESH_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchRows]);

  const c = rows.reduce((a, r) => {
    if (r.action === 'TAKE') { a.takes += 1; if (!r.exit_time) a.open += 1; }
    else a.skips += 1;
    if (r.exit_reason) a.exits += 1;
    if (r.ret_bp != null) a.net += r.ret_bp;
    return a;
  }, { takes: 0, skips: 0, open: 0, exits: 0, net: 0 });

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', background: T.bg }}>
    <div style={{ padding: 'clamp(12px, 3vw, 20px) clamp(10px, 4vw, 28px)', maxWidth: 1800, margin: '0 auto' }}>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap',
                    paddingBottom: 14, borderBottom: `1px solid ${T.border}`, marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.text }}>KUNBI Monitor</h1>
        <span style={{ fontSize: 11, color: T.textMuted }}>
          MW 1-min + 3-min filter · LONG bracket (+1.5/−2.0 ATR) · SHORT trail (2.5 ATR / init +1.5) ·
          cert EA615CBE
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center',
                      flexWrap: 'wrap', justifyContent: 'flex-end', flex: '1 1 360px' }}>
          <label style={{ fontSize: 11, color: T.textMuted }}>From</label>
          <input type="date" value={dateFrom} max={todayETStr()} onChange={e => setDateFrom(e.target.value)}
                 style={{ padding: '4px 8px', fontSize: 12, borderRadius: 4, border: `1px solid ${T.border}`,
                          background: T.bg, color: T.text, fontFamily: 'Roboto Mono, monospace' }} />
          <label style={{ fontSize: 11, color: T.textMuted }}>To</label>
          <input type="date" value={dateTo} max={todayETStr()} onChange={e => setDateTo(e.target.value)}
                 style={{ padding: '4px 8px', fontSize: 12, borderRadius: 4, border: `1px solid ${T.border}`,
                          background: T.bg, color: T.text, fontFamily: 'Roboto Mono, monospace' }} />
          <button onClick={() => { setDateFrom(''); setDateTo(''); }}
                  style={{ padding: '4px 8px', fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                           textTransform: 'uppercase', borderRadius: 4, cursor: 'pointer',
                           border: `1px solid ${T.border}`, background: 'transparent', color: T.textMuted }}>Clear</button>
          <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
                  style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4, border: `1px solid ${T.border}`,
                           background: T.bg, color: T.text }}>
            <option value="">All actions</option>
            <option value="TAKE">TAKE only</option>
            <option value="SKIP">SKIP only</option>
          </select>
          <select value={dirFilter} onChange={e => setDirFilter(e.target.value)}
                  style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4, border: `1px solid ${T.border}`,
                           background: T.bg, color: T.text }}>
            <option value="">Both dirs</option>
            <option value="LONG">LONG only</option>
            <option value="SHORT">SHORT only</option>
          </select>
          <select value={limit} onChange={e => setLimit(parseInt(e.target.value, 10))}
                  style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4, border: `1px solid ${T.border}`,
                           background: T.bg, color: T.text }}>
            <option value={50}>50 rows</option><option value={100}>100 rows</option>
            <option value={200}>200 rows</option><option value={500}>500 rows</option>
          </select>
          <button onClick={fetchRows} disabled={loading}
                  style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em',
                           textTransform: 'uppercase', borderRadius: 4, cursor: loading ? 'wait' : 'pointer',
                           border: 'none', background: `${KUNBI_COLOR}2e`, color: KUNBI_COLOR }}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: 10, marginBottom: 12, borderRadius: 4, background: 'rgba(239,68,68,0.1)',
                      border: `1px solid ${T.bear}`, color: T.bear, fontSize: 12 }}>Error: {error}</div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <Stat label="Signals" value={rows.length} color={T.text} />
        <Stat label="TAKE" value={c.takes} color={KUNBI_COLOR} />
        <Stat label="Open slot" value={c.open} color={STATUS_COLORS.OPEN} />
        <Stat label="Exits" value={c.exits} color={T.bull} />
        <Stat label="SKIP" value={c.skips} color={T.textMuted} />
        <Stat label="Net bp" value={num(c.net, 1)} color={c.net >= 0 ? T.bull : T.bear} />
      </div>

      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 8, fontFamily: 'Roboto Mono, monospace' }}>
        Shadow log · {rows.length} rows · newest first
        {lastFetched && ` · updated ${lastFetched.toLocaleTimeString('en-GB', { hour12: false })} ET`}
        <span style={{ marginLeft: 10 }}>· exits: <span style={{ color: T.bull }}>target/trail-stop</span> ·{' '}
          <span style={{ color: T.bear }}>stop/init-stop</span></span>
      </div>

      <div style={{ overflowX: 'auto', border: `1px solid ${T.border}`, borderRadius: 6,
                    background: 'rgba(255,255,255,0.01)' }}>
        <table style={{ width: '100%', minWidth: 1500, borderCollapse: 'collapse',
                        fontSize: 11, fontFamily: 'Roboto Mono, monospace' }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.04)', textAlign: 'left', color: T.textMuted,
                         fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              <th style={{ padding: '8px 10px' }}>Time (ET)</th>
              <th style={{ padding: '8px 10px' }}>Dir</th>
              <th style={{ padding: '8px 10px' }}>Tier</th>
              <th style={{ padding: '8px 10px' }}>Status</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Entry $</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Target $</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Stop $</th>
              <th style={{ padding: '8px 10px' }}>Exit (ET)</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Exit $</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Ret bp</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Hold m</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>ADX</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>dist</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>macd bp</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>VIX %</th>
              <th style={{ padding: '8px 10px' }}>Reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={16} style={{ padding: '20px 14px', color: T.textMuted, textAlign: 'center',
                                            fontStyle: 'italic' }}>
                No KUNBI decisions yet. Wire the Kunbi emitter to the webhook; every MW 1-min signal
                logs here (TAKE/SKIP) and its exit is backfilled by the walker. Auto-refresh {REFRESH_MS/1000}s.
              </td></tr>
            )}
            {rows.map(r => {
              const st = r.status || (r.action === 'TAKE' ? 'OPEN' : 'SKIP');
              const stColor = STATUS_COLORS[st] || T.textMuted;
              const stLabel = STATUS_LABEL[st] || st;
              const profit = r.ret_bp != null;
              return (
                <tr key={r.id} style={{ borderTop: `1px solid ${T.border}`,
                                        background: (r.action === 'TAKE' && !r.exit_time) ? 'rgba(59,130,246,0.06)' : 'transparent' }}>
                  <td style={{ padding: '6px 10px', color: T.text }}>{fmtET(r.bar_time)}</td>
                  <td style={{ padding: '6px 10px' }}>
                    <Pill value={r.dir} color={r.dir === 'LONG' ? T.bull : T.bear} />
                  </td>
                  <td style={{ padding: '6px 10px' }}>
                    <Pill value={r.tier} color={r.tier === 'A' ? T.bull : r.tier === 'C' ? T.bear : KUNBI_COLOR} />
                  </td>
                  <td style={{ padding: '6px 10px' }}><Pill value={stLabel} color={stColor} mono /></td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.text }}>{num(r.entry_price)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.textMuted }}>{num(r.target_price)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.bear }}>{num(r.stop_price)}</td>
                  <td style={{ padding: '6px 10px', color: T.textMuted }}>{r.exit_time ? fmtET(r.exit_time) : '—'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.text }}>{num(r.exit_price)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700,
                               color: !profit ? T.textMuted : r.ret_bp >= 0 ? T.bull : T.bear }}>
                    {profit ? num(r.ret_bp, 1) : '—'}
                  </td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.textMuted }}>
                    {r.hold_min != null ? r.hold_min : '—'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.text }}>{num(r.adx, 1)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.text }}>{num(r.dist_ema200, 2)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right',
                               color: r.macd_bp != null && r.macd_bp < -4.8 ? T.bear : T.text }}>
                    {num(r.macd_bp, 1)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.text }}>
                    {r.vix_pctile != null ? (r.vix_pctile * 100).toFixed(0) : '—'}</td>
                  <td style={{ padding: '6px 10px', color: T.textMuted, fontSize: 10, maxWidth: 200,
                               overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={r.reason || ''}>{r.reason || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
    </div>
  );
}
